import { cardDeleteId, cardDetailId, cardEditId } from "@/assets/googleTrackIds";
import { CouponStatus, CouponType, CouponUsed } from "@payping/api";
import { Add, CuToman } from "@payping/icons";
import { addOverflow } from "@payping/utils/string";
import { getCoupons, removeCoupon } from "@payping/redux/actions";
import { submitQueryParams } from "@payping/utils/query-string";
import {
  Button,
  Col,
  Copy,
  Empty,
  Flex,
  LegacyCard,
  LegacyCardList,
  Modal,
  Row,
  Space,
  Tag,
  Typography,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import qs from "query-string";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { DisplayModeEnum } from "@payping/web-components/legacyComponents/List/types";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";
import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import CouponFilter from "@/components/DashProduct/CouponListFilter";
import styles from "@/assets/scss/Shop/Product/index.module.scss";

import {
  ListBoolQueryParamsType,
  ListNumberQueryParamsType,
  ListStringQueryParamsType,
} from "@/pages/Shop/Coupon/types";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import { DashboardRoutes } from "@/router";
import { ListBulkActions } from "./listBulkActions";
import Detail from "./Detail";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

// TODO: FIX CONTENT
const CouponList: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  // Because there are different types of filters, we should separate parsing them to avoid conflict between types (like converting "241e43" string to scientific number 2.41e45)
  const parsedSearch = qs.parse(location.search);
  const { searchText }: ListStringQueryParamsType = qs.parse(location.search);
  const { pageSize = 12, pageNumber = 1 }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });
  const { isArchived }: ListBoolQueryParamsType = qs.parse(location.search, { parseBooleans: true });
  const [detailCode, setDetailCode] = useState<string>();
  const coupons = useAppSelector((state) => state.coupons);
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();

  const { loading, retry } = useMessagedAsyncRetry(
    () =>
      dispatch(
        getCoupons({
          couponUsed: CouponUsed.All,
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
          searchText,
          withArchived: isArchived,
        }),
      ),
    [dispatch, pageSize, pageNumber, searchText, isArchived],
    { success: false, useProgress: true },
  );

  async function handleDelete(code: string): Promise<void> {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("message.remove", { subject: t("coupon.title") }),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => {
        await dispatch(
          removeCoupon(code, {
            couponUsed: CouponUsed.All,
            limit: pageSize,
            offset: (pageNumber - 1) * pageSize,
            searchText,
            withArchived: isArchived,
          }),
        );
        await retry();
      },
    });
  }

  function getTagColor(name: string) {
    switch (true) {
      // TODO: sadly backend returns persian name
      case name === "همه آیتم‌ها":
      case name === "همه فاکتورها":
        return "blue";
      default:
        return "magenta";
    }
  }

  const data: ListCardProps["dataSource"] = coupons.list.map((coupon, index) => ({
    key: String(coupon.code),
    title: (
      <Space>
        <Typography.Title level={4}>
          {coupon.type === CouponType.Percent ? (
            `${coupon.amount}%`
          ) : (
            <Flex align="center">
              <Typography.Text>{`${formatLocalizedNumber({ value: coupon.amount ?? 0, options: { maximumFractionDigits: 2 } })}`}</Typography.Text>
              <CuToman width={24} height={24} />
            </Flex>
          )}
        </Typography.Title>
        <Tag>{t(`coupon.status.${CouponStatus[coupon.status || CouponStatus.ACTIVE]}`)}</Tag>
      </Space>
    ),
    content: [
      <div key={index} className={styles.couponProducts}>
        {coupon.activeProductName?.map((p, i) => (
          <Tag key={i} color={getTagColor(p)}>
            {addOverflow(p || "", 20)}
          </Tag>
        ))}
      </div>,
    ],
    descriptions: (
      <Copy text={coupon.userCouponCode || ""}>
        <Typography.Text className={"font-em-13"}>{coupon.userCouponCode}</Typography.Text>
      </Copy>
    ),

    actions: [
      <Space key={index}>
        <a onClick={() => setDetailCode(coupon.code)} key={0} data-google-track={cardDetailId}>
          {t("details")}
        </a>
        <Link
          to={{
            pathname: DashboardRoutes.myShop.coupon.edit.url.replace(":code", coupon.code || ""),
          }}
          state={{ locationSearch: location.search }}
          key={1}
          data-google-track={cardEditId}
        >
          {t("edit")}
        </Link>
        <Typography.Link
          disabled={selectedRowKeys.length > 1}
          key={1}
          onClick={(): Promise<void> => handleDelete(coupon.code || "")}
          data-google-track={cardDeleteId}
        >
          {t("delete")}
        </Typography.Link>
      </Space>,
    ],
  }));

  return (
    <HandlingCrispInMobile>
      <>
        {loading || data?.length > 0 || location.search ? (
          <>
            <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
              <CouponFilter loading={loading} retry={retry} />
              <LegacyCardList
                loading={!data.length ? loading : false}
                dataSource={data}
                grid={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 4, xxxl: 4 }}
                pagination={{
                  onChange: (page, newSize): void => {
                    setSelectedRowKeys([]);
                    const newValues = {
                      ...parsedSearch,
                      pageNumber: page,
                      pageSize: newSize,
                    };
                    submitQueryParams({
                      navigate,
                      newValues,
                      parsedSearch,
                      pathname: location.pathname,
                      retry,
                    });
                  },
                  onShowSizeChange: (_currentPage, newSize): void => {
                    const newValues = {
                      ...parsedSearch,
                      pageNumber: 1,
                      pageSize: newSize,
                    };
                    submitQueryParams({
                      navigate,
                      newValues,
                      parsedSearch,
                      pathname: location.pathname,
                      retry,
                    });
                  },
                  pageNumber,
                  pageSize,
                  total: coupons.totalCount,
                }}
                itemSelection={{
                  onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys.map((r) => String(r))),
                  selectedItemKeys: selectedRowKeys,
                }}
                mode={DisplayModeEnum.Card}
              />
            </Space>
          </>
        ) : (
          <Empty
            description={
              <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
                <Typography.Text strong>{t("coupon.emptyList.title")}</Typography.Text>
                <Typography.Text>{t("coupon.emptyList.content")}</Typography.Text>
                <Button
                  key={0}
                  type={"primary"}
                  onClick={(): void => {
                    navigate(
                      {
                        pathname: DashboardRoutes.myShop.coupon.create.url,
                      },
                      { state: { locationSearch: location.search } },
                    );
                  }}
                  icon={<Add width={14} height={14} />}
                >
                  {` ${t("add.button", { subject: t("coupon.title") })}`}
                </Button>
              </Space>
            }
          >
            <Row gutter={[8, 8]} className={"tw:w-full"}>
              <Col xs={24} md={12}>
                <LegacyCard.Ant className="tw:text-start">
                  <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
                    <Typography.Text type={"secondary"} className={"tw:text-xs"}>
                      {t("coupon.emptyList.cards.discount.upperTitle")}
                    </Typography.Text>
                    <Typography.Text strong> {t("coupon.emptyList.cards.discount.title")}</Typography.Text>
                    <Typography.Text type={"secondary"}>{t("coupon.emptyList.cards.discount.content")}</Typography.Text>
                    <Space>
                      <Link to={DashboardRoutes.connections.developers.root.url}>
                        <Button type={"link"} className={"tw:p-0!"}>
                          {t("common.receiveToken")}
                        </Button>
                      </Link>

                      <Link to={DashboardRoutes.connections.developers.root.url}>
                        <Button type={"link"} className={"tw:p-0!"}>
                          {t("common.guideToTechnicalDocumentation")}
                        </Button>
                      </Link>
                    </Space>
                  </Space>
                </LegacyCard.Ant>
              </Col>
              <Col xs={24} md={12}>
                <LegacyCard.Ant className="tw:text-start">
                  <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
                    <Typography.Text type={"secondary"} className={"tw:text-xs"}>
                      {t("coupon.emptyList.cards.plugins.upperTitle")}
                    </Typography.Text>
                    <Typography.Text strong> {t("coupon.emptyList.cards.plugins.title")}</Typography.Text>
                    <Typography.Text type={"secondary"}>{t("coupon.emptyList.cards.plugins.content")} </Typography.Text>
                    <Link to={DashboardRoutes.connections.developers.root.url}>
                      <Button type={"link"} className={"tw:p-0!"}>
                        {t("common.readMore")}
                      </Button>
                    </Link>
                  </Space>
                </LegacyCard.Ant>
              </Col>
            </Row>
          </Empty>
        )}
      </>
      <ListBulkActions
        data={data}
        retry={retry}
        isArchived={isArchived}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      {detailCode ? <Detail code={detailCode} setCode={setDetailCode} retryList={retry} /> : null}
      {contextHolder}
    </HandlingCrispInMobile>
  );
};

export default CouponList;
